import { Container } from 'containers/Container';
import * as React from 'react';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  CardText,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { CognitoUser } from '../../app/Login/lib/CognitoUser';
import { HttpRequest } from '../../util/http';
import { userData } from '../../util/userData';
import { generateUUID } from '../../util/uuid';
import { updatePassword } from '../UserAdministrationContainer/lib/http';
import { ResetPasswordComponent } from './lib/resetPasswordComponent';

interface IProfileState {
  username: string
  email: string
  phone: string
  newEmail: string
  receiveNotifications: number
  actionAlertUserNotifications: number
  submitting: boolean
  actionAlertButtonSubmitting: boolean
  showChangeEmail: boolean
  showResetPassword: boolean
  resetPasswordError: boolean
  showNotificationMessage: boolean
  changePasswordNotification: string
  password: string
  confirmPassword: string
  confirmation: string
  showMismatchedPasswords: boolean
  resetPasswordSubmitting: boolean
  openResetPasswordSubmitting: boolean
  changeEmailSubmitting: boolean
  showChangeEmailNotification: boolean
  changeEmailNotification: string
  showDoneButton: boolean
  confirmLastAdminUser: boolean
  displayActionAlertNotification: boolean
  isApiOpen: boolean
  isTermAgreementOpen: boolean
  apiKey: string
  apiSecret: string
  apiAccess: boolean
  mfaEnabled: boolean
  hasApiKey: boolean
  eulaSigned: boolean
  disable: boolean
  mfaSelectedOption?: 'email' | 'phone'
  mfaValidationStatus?: string
  mfaChangedFlag: boolean
}

interface ISaveNotificationResponse {
  code: number
  receive_notifications?: string
  actionAlertUserNotifications?: string
}

interface IProfileResponse {
  user: IUser
}

interface IUser {
  cellPhoneNumber: string;
  username: string
  email: string
  receive_notifications: string
  actionAlertUserNotification?: string
  displayActionAlertNotification: boolean
  apiKey: string
  apiSecret: string
  apiAccess: boolean
  hasApiKey: boolean
  eulaSigned: boolean
  disable: boolean
  mfaEnabled: boolean
  mfaMethod?: 'email' | 'phone'
  mfaStatus?: string
}

const isValidPhoneNumber = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  const regex = /^(1)?([2-9]\d{2})([2-9]\d{2})(\d{4})$/;
  return regex.test(cleaned);
}

const DisplayOrEditComponent = (props: { fieldName: 'Email' | 'Cell', fieldValue: string, updateFieldValue: (fieldValue: string) => void }) => {
  const { fieldName, fieldValue } = props
  const [edit, setEdit] = React.useState(false);
  const [input, setInput] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  if (!edit) {
    if (fieldValue === '' || fieldValue === null || fieldValue === undefined) {
      return (
        <p>{fieldName}: <span>Not Set</span>&nbsp;
          <button className="btn btn-action" onClick={() => setEdit(true)}>Edit</button>
        </p>
      );
    } else {
      return (
        <p>{fieldName}: <span>{fieldValue}</span>&nbsp;
          <button className="btn btn-action" onClick={() => setEdit(true)}>Edit</button>
        </p>
      );
    }
  } else {
    return (
      <div>
        <form>
          <div className="form-group">
            <label htmlFor="profileInputField" style={{ color: 'white' }}>{fieldName}: {fieldValue ? `${fieldValue}` : 'Not set'}</label>
            {fieldName === 'Email' ?
              <input type="email" className="form-control" id="profileInputField" aria-describedby="emailHelp" placeholder="Enter email" onChange={(event) => {
                setInput(event.currentTarget.value);
              }} />
              :
              <input type="tel" className="form-control" id="profileInputField" aria-describedby="emailHelp"
                placeholder="Enter 10-digit cell phone number"
                onKeyPress={(event) => {
                  // Only allow numeric key presses
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
                onChange={(event) => {
                  setInput(event.currentTarget.value);
                }} />
            }
            {fieldName === 'Cell' && input.length > 0 && !isValidPhoneNumber(input) &&
              <div style={{ marginTop: '5px' }}>
                Please enter exactly 10 digits (spaces and special characters will be removed)
              </div>
            }
          </div>
          <button className={`btn btn-primary ${input.length > 0 ? '' : 'disabled'}`}
            onClick={(event) => {
              event.preventDefault();

              // Phone validation
              if (fieldName === 'Cell' && !isValidPhoneNumber(input)) {
                window.alert('Please enter exactly 10 digits for the phone number');
                return;
              }

              const method = 'POST';
              let requestParams = {};
              let route = '';

              if (fieldName === 'Email') {
                requestParams['email'] = input;
                route = 'email';
              } else {
                // Clean the phone number before sending
                requestParams['phone'] = `1${input.replace(/\D/g, '')}`;
                route = 'phone';
              }

              if (input.length > 0) {
                // Show a loading indicator
                setSubmitting(true);

                userData.getUserId()
                  .then((userId) => {
                    requestParams['userId'] = userId;
                    return HttpRequest.fetch(`/api/user/update/${route}`, { method, data: requestParams });
                  })
                  .then((apiResponse: any) => {
                    setSubmitting(false);

                    if (apiResponse.success) {
                      setInput('');
                      setEdit(false);
                      props.updateFieldValue(fieldName === 'Cell' ? input.replace(/\D/g, '') : input);
                    } else {
                      window.alert(`Error: ${apiResponse.message || 'Unable to update profile'}`);
                    }
                  })
                  .catch((err) => {
                    setSubmitting(false);
                    console.error('Error:', err);

                    const errorMessage = err.toString().includes('<')
                      ? 'Server returned an HTML error page. Please contact support.'
                      : err.message || 'Unknown error';

                    window.alert(`There was an error updating your profile: ${errorMessage}`);
                  });
              }
            }}>
            {submitting ?
              <>Saving... <i className="fas fa-fw fa-cog fa-spin" /></> :
              'Submit'
            }
          </button>
        </form>
      </div>
    );
  }
}

export class ProfileContainer extends Container<{}, IProfileState> {
  state: IProfileState = {
    username: '',
    email: '',
    phone: '',
    newEmail: '',
    receiveNotifications: 0,
    actionAlertUserNotifications: 0,
    displayActionAlertNotification: false,
    submitting: false,
    actionAlertButtonSubmitting: false,
    showChangeEmail: false,
    showResetPassword: false,
    showNotificationMessage: false,
    changePasswordNotification: '',
    password: '',
    confirmPassword: '',
    confirmation: '',
    showMismatchedPasswords: false,
    resetPasswordSubmitting: false,
    openResetPasswordSubmitting: false,
    changeEmailSubmitting: false,
    showChangeEmailNotification: false,
    changeEmailNotification: '',
    showDoneButton: false,
    confirmLastAdminUser: false,
    isApiOpen: false,
    isTermAgreementOpen: false,
    apiKey: '',
    apiSecret: '',
    apiAccess: false,
    mfaEnabled: false,
    hasApiKey: false,
    eulaSigned: false,
    disable: false,
    resetPasswordError: false,
    mfaChangedFlag: false,
  };

  constructor(props) {
    super(props)
    this.loadProfile()
  }

  loadProfile() {
    HttpRequest.fetch<IProfileResponse>('/scripts/profile/loadProfile', {}).then((response) => {
      const receiveNotifications = response.user.receive_notifications === 'Yes' ? 1 : 0
      const actionAlertUserNotifications = response.user.actionAlertUserNotification === 'Yes' ? 1 : 0
      this.setState({
        username: response.user.username,
        email: response.user.email,
        phone: response.user.cellPhoneNumber,
        receiveNotifications,
        actionAlertUserNotifications,
        displayActionAlertNotification: response.user.displayActionAlertNotification,
        apiKey: response.user.apiKey,
        apiAccess: response.user.apiAccess,
        hasApiKey: response.user.hasApiKey,
        eulaSigned: response.user.eulaSigned,
        mfaEnabled: response.user.mfaEnabled,
      });
      if (response.user.mfaMethod) {
        this.setState({ mfaSelectedOption: response.user.mfaMethod });
      }
      if (response.user.mfaStatus) {
        this.setState({ mfaValidationStatus: response.user.mfaStatus });
      }
    })
  }

  toggleNotifications() {
    const { receiveNotifications } = this.state
    const state = receiveNotifications === 1 ? 0 : 1
    const request = { receive_notifications: state }
    const method = 'POST'
    const body = JSON.stringify(request)
    this.setState({ submitting: true })

    HttpRequest.fetch<ISaveNotificationResponse>('/scripts/profile/saveNotifications', { method, body, params: request }).then((response) => {
      this.setState({ receiveNotifications: response.receive_notifications === 'Yes' ? 1 : 0, submitting: false })
    })
  }

  toggleActionAlertNotifications() {
    const { actionAlertUserNotifications } = this.state
    const state = actionAlertUserNotifications === 1 ? 0 : 1
    const request = { actionAlertUserNotifications: state }
    const method = 'POST'
    const body = JSON.stringify(request)
    this.setState({ actionAlertButtonSubmitting: true })

    //NOTE: Only need to check if we are disabling action alert notes
    if (state === 0) {
      HttpRequest.fetch<any>('/scripts/profile/checkActionAlertNotificationCount', { method }).then((response) => {
        const alertUser = true // response.alertUser
        if (response.alertUser) {
          this.setState({ confirmLastAdminUser: true })
        } else {
          this.saveActionAlertNotifications()
        }
      })
    } else {
      this.saveActionAlertNotifications()
    }
  }

  saveActionAlertNotifications() {
    const { actionAlertUserNotifications } = this.state
    const state = actionAlertUserNotifications === 1 ? 0 : 1
    const request = { actionAlertUserNotifications: state }
    const method = 'POST'
    const body = JSON.stringify(request)
    this.setState({ actionAlertButtonSubmitting: true })

    HttpRequest.fetch<ISaveNotificationResponse>('/scripts/profile/saveActionAlertUserNotification', { method, body, params: request }).then((response) => {
      this.setState({ actionAlertUserNotifications: response.actionAlertUserNotifications === 'Yes' ? 1 : 0, actionAlertButtonSubmitting: false, confirmLastAdminUser: false })
    })
  }

  saveEmail(e) {
    e.preventDefault()
    const { newEmail } = this.state

    this.setState({ changeEmailSubmitting: true })

    if (newEmail === '') {
      alert('Email address is empty');
      return;
    }

    CognitoUser.GetLoggedInUser().then(function (cognitoUser) {
      cognitoUser.updateEmail(newEmail).catch((err: Error) => {
        this.setState({ showChangeEmailNotification: true, changeEmailNotification: err.message })
        return
      })
    })

    const params = { email: newEmail }
    const method = 'POST'
    const body = JSON.stringify(params)

    HttpRequest.fetch('/scripts/profile/saveEmail', { method, body, params }).then(() => {
      this.setState({ newEmail: '', email: newEmail, changeEmailSubmitting: false, changeEmailNotification: 'Email has been updated.', showChangeEmailNotification: true })
      setTimeout(() => {
        this.setState({ showChangeEmail: false, changeEmailNotification: '', showChangeEmailNotification: false })
      }, 2000)
    }).catch((err: Error) => {
      this.setState({ showChangeEmailNotification: true, changeEmailNotification: err.message })
    })
  }

  resetPassword(e) {
    e.preventDefault();

    this.setState({ showChangeEmail: false, openResetPasswordSubmitting: true })

    this.setState({ showNotificationMessage: false, openResetPasswordSubmitting: false, showResetPassword: true, resetPasswordError: false })
  }

  toggleModal() {
    this.setState({ confirmLastAdminUser: !this.state.confirmLastAdminUser, actionAlertButtonSubmitting: false })
  }

  async confirmPassword(e) {
    e.preventDefault()

    const { password, confirmPassword } = this.state

    this.setState({ resetPasswordSubmitting: true })

    if (password !== confirmPassword) {
      this.setState({ changePasswordNotification: 'Your passwords do not match', resetPasswordSubmitting: false })
      return
    } else {
      this.setState({ changePasswordNotification: '' })
    }

    try {
      userData.getUserId().then((userId) => {
        updatePassword(userId, password).then((success) => {
          if (success) {
            //NOTE: Display success message. Then redirect to login page.
            const showNotificationMessage = true
            const changePasswordNotification = 'Password updated successfully.'
            this.setState({ showNotificationMessage, changePasswordNotification, resetPasswordSubmitting: false, showDoneButton: true, confirmation: '', confirmPassword: '', password: '' })
          }
        })
      })
    } catch (err) {
      const showNotificationMessage = true
      const changePasswordNotification = (err.message ? err.message : 'There was an error.')
      this.setState({ showNotificationMessage, changePasswordNotification, resetPasswordSubmitting: false })
    }
  }

  ///////////// Generate API Keys //////////////////
  generateApiKeys = () => {
    this.setState({ isTermAgreementOpen: false })
    // tslint:disable-next-line:variable-name
    const api_key = generateUUID();
    // tslint:disable-next-line:variable-name
    const api_secret = generateUUID();
    const { username } = this.state;
    const { email } = this.state;

    this.setState({ disable: true });
    this.setState({ apiKey: api_key });
    this.setState({ apiSecret: api_secret });

    const data = { api_key, api_secret, username, email }
    const method = 'POST'
    HttpRequest.fetch('/scripts/api/generateApiKey', { method, data }).then((response) => {
      // console.log(response, ' response from generate api key')
    }).catch((err) => {
      // console.log(err)
    })
    this.setState({ isApiOpen: true });
    // tslint:disable-next-line:ban-comma-operator
    setTimeout(() => { this.setState({ hasApiKey: true }), 3000 }); //fixme: second arg for setTimeout is misplaced
  }

  //////////////// Reset API Secret ///////////////
  resetApiSecret = () => {
    // tslint:disable-next-line:variable-name
    const api_secret = generateUUID();
    const { username } = this.state;
    const { email } = this.state;
    const { apiKey } = this.state;
    this.setState({ apiSecret: api_secret });
    const data = { apiKey, api_secret, username, email }
    const method = 'POST'
    HttpRequest.fetch('/scripts/api/resetApiSecret', { method, data }).then((response) => {
      // console.log(response, " response from reset api secret")
    }).catch((err) => {
      // console.log(err)
    })
    this.setState({ isApiOpen: true });
  }

  //////////////// Modal for API Secret ///////////////
  apiModalOpen = () => {
    this.setState({ isApiOpen: true });
    // console.log('this modal open');
  }
  apiModalClose = () => {
    this.setState({ isApiOpen: false });
    // console.log('this modal closed');
  }
  ///////// Modal for Terms and Agreement ////////////
  termsAgreementModalOpen = () => {
    this.setState({ isTermAgreementOpen: true });
    // console.log('this terms modal open');
  }
  termsAgreementModalClose = () => {
    this.setState({ isTermAgreementOpen: false });
    // console.log('this terms modal closed');
  }
  /////////////////////////////////////////////////////

  render() {
    const { receiveNotifications, showChangeEmail, showResetPassword, actionAlertUserNotifications, displayActionAlertNotification, apiAccess, hasApiKey, eulaSigned, mfaEnabled } = this.state
    const notificationText = receiveNotifications === 1 ? 'Yes' : 'No'
    const notificationButtonText = receiveNotifications === 1 ? 'Disable' : 'Enable'

    const actionAlertEmailText = actionAlertUserNotifications === 1 ? 'Yes' : 'No'
    const actionAlertButtonText = actionAlertUserNotifications === 1 ? 'Disable' : 'Enable'

    return (
      <div className="blacklight" style={{ padding: '20px' }}>
        <div className="container">
          <Modal wrapClassName="blacklight" modalClassName="blacklight-modal" isOpen={this.state.confirmLastAdminUser}
            toggle={() => this.toggleModal()}>
            <ModalHeader>
              Warning
            </ModalHeader>
            <ModalBody>
              You are currently the only admin receiving these notifications. If you turn it off, no admins will be
              notified when users assign action alerts to people who are not in the system.
            </ModalBody>
            <ModalFooter>
              <Button color="action" onClick={() => this.saveActionAlertNotifications()}>Confirm</Button>
              <Button color="secondary" onClick={() => this.toggleModal()}>Cancel</Button>
            </ModalFooter>
          </Modal>
          <div className="row">

            <div className="col s12 m6">
              <div className="card" style={{ backgroundColor: 'var(--brand-ink)' }}>
                <div className="card-content white-text">
                  <span className="card-title">User Information</span>
                  <p>Username: <span>{this.state.username}</span></p>
                  <DisplayOrEditComponent
                    fieldName="Email"
                    fieldValue={this.state.email}
                    updateFieldValue={(fieldValue) => this.setState({ email: fieldValue })}
                  />
                  <DisplayOrEditComponent
                    fieldName="Cell"
                    fieldValue={this.state.phone}
                    updateFieldValue={(fieldValue) => this.setState({ phone: fieldValue })}
                  />
                </div>
                <div className="card-action">
                  {/* <Button onClick={(e) => this.setState({ showChangeEmail: true, showResetPassword: false })}>Change Email</Button>&nbsp; */}
                  <Button color="action" disabled={this.state.openResetPasswordSubmitting}
                    onClick={(e) => this.resetPassword(e)}>
                    {this.state.openResetPasswordSubmitting ?
                      <>
                        <span>Processing...&nbsp;</span>
                        <i className="fas fa-fw fa-cog fa-spin" />
                      </>
                      :
                      <>
                        <span>Reset Password&nbsp;</span>
                      </>
                    }
                  </Button>
                </div>
              </div>
            </div>

            <div className="col s12 m6">
              <div className="card" style={{ backgroundColor: 'var(--brand-ink)' }}>
                <div className="card-content white-text" style={{ padding: '20px', minHeight: '131px' }}>
                  <span className="card-title">Notifications</span>
                  <p>Would you like to receive notifications when new feedback is available? <span
                    style={{ fontWeight: 'bold', fontSize: '1.2rem' }}>{notificationText}</span></p>
                </div>
                <div className="card-action">
                  <Button color="action" disabled={this.state.submitting} onClick={this.toggleNotifications.bind(this)}>
                    {this.state.submitting ?
                      <>
                        <span>Submiting...&nbsp;</span>
                        <i className="fas fa-fw fa-cog fa-spin" />
                      </>
                      :
                      <>
                        <span>{notificationButtonText}&nbsp;</span>
                        <i className="fas fa-fw fa-paper-plane" />
                      </>
                    }
                  </Button>
                </div>
              </div>
            </div>

          </div>

          <div className="row">
            {apiAccess ?
              <div className="col s12 m6">
                <div className="card" style={{ backgroundColor: 'var(--brand-ink)' }}>
                  <div className="card-content white-text">
                    <span className="card-title">API Access</span>
                    <p>API Key:
                      &nbsp;
                      <span>{this.state.apiKey}</span>
                    </p>
                    <p>
                      &nbsp;
                      {/* <span className="red-text2">{this.state.apiSecret}</span> */}
                    </p>
                  </div>
                  <div className="card-action">
                    {!hasApiKey ?
                      <Button
                        disabled={this.state.disable}
                        color="action"
                        onClick={this.termsAgreementModalOpen}
                      >
                        Generate API Keys
                      </Button>
                      :
                      <Button
                        color="action"
                        // onClick={this.termsAgreementModalOpen}
                        onClick={this.resetApiSecret}
                      >
                        Reset Api Secret
                      </Button>
                    }
                  </div>
                </div>
              </div>
              : <div className="col s12 m6"></div>}

            {displayActionAlertNotification ?
              <div className="col s12 m6">
                <div className="card" style={{ backgroundColor: 'var(--brand-ink)' }}>
                  <div className="card-content white-text" style={{ padding: '20px', minHeight: '131px' }}>
                    <span className="card-title" style={{ color: '--brand-pink' }}>Action Alert Assigned to non-User</span>
                    <p>Be notified when someone asigns an action alert to a non-user and gives them Alerts Only
                      access?&nbsp;<span
                        style={{ fontWeight: 'bold', fontSize: '1.2rem' }}>{actionAlertEmailText}</span></p>
                  </div>
                  <div className="card-action">
                    <Button color="action" disabled={this.state.submitting}
                      onClick={this.toggleActionAlertNotifications.bind(this)}>
                      {this.state.actionAlertButtonSubmitting ?
                        <>
                          <span>Submitting...&nbsp;</span>
                          <i className="fas fa-fw fa-cog fa-spin" />
                        </>
                        :
                        <>
                          <span>{actionAlertButtonText}&nbsp;</span>
                          <i className="fas fa-fw fa-paper-plane" />
                        </>
                      }
                    </Button>
                  </div>
                </div>
              </div>
              : null}
          </div>

          <div className="row">
            {mfaEnabled ?
              <div className="col s12">
                <div className="card" style={{ backgroundColor: 'var(--brand-ink)' }}>
                  <div className="card-content white-text">
                    <span className="card-title">Multi Factor Authentication</span>
                    <p>Would you like to enable SMS multi-factor authentication? (cell phone required)</p>

                    {!isValidPhoneNumber(this.state.phone) &&
                      <div className="alert alert-warning">
                        You need to add a valid 10-digit phone number before enabling SMS authentication.
                      </div>
                    }

                    <div className="blacklightz">
                      <form>
                        <input type="checkbox" id="mfaSMSOption" style={{ opacity: 0 }}
                          disabled={!isValidPhoneNumber(this.state.phone)}
                          checked={this.state.mfaSelectedOption === 'phone'}
                          onChange={() => {
                            this.setState({ mfaChangedFlag: true });
                            this.setState({ mfaSelectedOption: 'phone' });
                          }} />
                        <label htmlFor="mfaSMSOption" className={`${!isValidPhoneNumber(this.state.phone) ? 'text-muted' : 'white-text'}`}>Yes</label>
                      </form>
                      <form>
                        <input type="checkbox" id="mfaEmailOption" style={{ opacity: 0 }}
                          checked={this.state.mfaSelectedOption === 'email'}
                          onChange={() => {
                            this.setState({ mfaChangedFlag: true });
                            this.setState({ mfaSelectedOption: 'email' });
                          }} />
                        <label htmlFor="mfaEmailOption" className="white-text">No</label>
                      </form>
                    </div>
                  </div>
                  <div className="card-action">
                    {this.state.mfaChangedFlag &&
                      <Button color="action" onClick={() => {
                        const method = 'POST';
                        userData.getUserId().then((userId) => {
                          HttpRequest.fetch('/api/user/update/mfa', { method, data: { userId, method: this.state.mfaSelectedOption } }).then((response) => {
                            // @ts-ignore
                            if (response.success) {
                              this.setState({ mfaChangedFlag: false });
                            }
                          });
                        });

                      }}>Save Changes</Button>
                    }
                  </div>
                </div>
              </div>
              : <div className="col s12"></div>}
          </div>
          {/* This is to generate Api Keys */}
          {this.state.isApiOpen ? (
            <div className="row">
              <div className="col s12 m6">
                <div className="card" style={{ backgroundColor: 'var(--brand-ink)' }}>
                  <div className="card-content white-text">
                    <span className="card-title">
                      API Secret
                    </span>
                    <p>API Secret:
                      &nbsp;
                      <span>{this.state.apiSecret}</span>
                    </p>
                    <p className="red-text2">Please copy the Api Secret! It will not be shown again.
                    </p>
                    <div className="card-action">
                      <Button onClick={this.apiModalClose}>
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col s12 m6"></div>
            </div>
          ) : null}

        </div>

        {showChangeEmail ?
          <Card>
            <Form onSubmit={(e) => this.saveEmail(e)}>
              <CardBody>
                <h4>Change Email</h4>
                {this.state.showChangeEmailNotification ?
                  <ul style={{ backgroundColor: '#ff5800', padding: '10px', color: '#fff', fontWeight: 'bold' }}>
                    {
                      this.state.showChangeEmailNotification ?
                        <li style={{ backgroundColor: '#FF5800' }}>{this.state.changeEmailNotification}</li> :
                        null
                    }
                  </ul>
                  : null}
                <Row>
                  <Col sm={12}>
                    <FormGroup>
                      <Label>Email</Label>
                      <Input type="email" value={this.state.newEmail}
                        onChange={(e) => this.setState({ newEmail: e.currentTarget.value })} />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter>
                <Button color="action" disabled={this.state.changeEmailSubmitting}>
                  {this.state.changeEmailSubmitting ?
                    <>
                      <span>Submiting...&nbsp;</span>
                      <i className="fas fa-fw fa-cog fa-spin" />
                    </>
                    :
                    <>
                      <span>Submit&nbsp;</span>
                      <i className="fas fa-fw fa-paper-plane" />
                    </>
                  }
                </Button>&nbsp;
                <Button color="white" onClick={(e) => this.setState({ changeEmailSubmitting: false, showChangeEmail: false })}>Cancel</Button>
              </CardFooter>
            </Form>
          </Card> : null
        }
        {showResetPassword ? (
          <ResetPasswordComponent
            onSubmit={(e) => this.confirmPassword(e)}
            showNotificationMessage={this.state.showNotificationMessage}
            changePasswordNotification={this.state.changePasswordNotification}
            username={this.state.username}
            confirmationCode={this.state.confirmation}
            onChange={(e) =>
              this.setState({ confirmation: e.currentTarget.value })
            }
            password={this.state.password}
            setPassword={(e) =>
              this.setState({ password: e.currentTarget.value })
            }
            confirmationPassword={this.state.confirmPassword}
            setConfirmationPassword={(e) =>
              this.setState({ confirmPassword: e.currentTarget.value })
            }
            showDoneButton={this.state.showDoneButton}
            onClick={(e) =>
              this.setState({ showResetPassword: false, showDoneButton: false })
            }
            disabled={this.state.resetPasswordSubmitting}
            resetPasswordError={this.state.resetPasswordError}
            disableConfirmationCode={true}
          />
        ) : null}

        {this.state.isTermAgreementOpen ?
          <Card style={{ zIndex: 9999, marginTop: "-400px", width: '75%', marginLeft: '12.5%', backgroundColor: 'var(--brand-ink)', boxShadow: "rgba(0, 0, 0, 0.35) 0px 5px 15px" }}>
            <CardBody>
              <h4 style={{ textAlign: 'center', marginTop: "12px", color: "white" }}>AVANNIS LLC</h4>
              <h5 style={{ textAlign: 'center', marginBottom: "10px", color: "white" }}>APPLICATION PROGRAMMING INTERFACE END USER LICENSE AGREEMENT</h5>
              <Col style={{ height: "300px", overflow: "scroll", backgroundColor: 'white', fontSize: '12px' }}>
                <CardText>
                  <p>
                    <em><b>IMPORTANT - READ CAREFULLY THE FOLLOWING APPLICATION PROGRAMMING INTERFACE END USER LICENSE AGREEMENT</b></em> (the "Agreement") before installing or using the licensed application programming interface (the "API"). The terms of this Agreement constitute the complete and exclusive statement of the agreement between you, or the company on whose behalf you are installing or using the API, ("you" or “your”) and the developer of the API, Avannis LLC (the "Company"), which supersedes any proposal or prior agreement, oral or written, and any other communication relating to the subject matter of this Agreement.
                  </p>
                  <p>
                    By clicking the "I agree" button (or any similar button), or by downloading, installing or using the API, you agree to be bound by this Agreement. If you do not agree to the terms of this Agreement, click the "cancel" button (or any similar button) and the download and installation process will not begin, and do not use the API. Do not select "I agree", download, install or use the API until you have carefully read, understood, and agreed to this Agreement. FURTHERMORE, YOU HEREBY WAIVE ANY RIGHTS OR REQUIREMENTS UNDER ANY LAWS OR REGULATIONS IN ANY JURISDICTION WHICH REQUIRE AN ORIGINAL (NON-ELECTRONIC) SIGNATURE OR DELIVERY OR RETENTION OF NON-ELECTRONIC RECORDS, TO THE EXTENT PERMITTED UNDER APPLICABLE LAW.
                  </p>
                  <p>
                    This Agreement together with any additional terms of use (the “Terms”) located at the Company’s website located at (https://us.avannis.com/) (the “Site”), govern your use of the API and any products or services provided or made available through the API and the Site. By installing and/or using the API you agree to also be legally bound by these Terms, which are expressly incorporated herein by reference and made a part of this Agreement.
                  </p>
                  <ol>
                    <li>
                      <b>GRANT OF LICENSE.</b> Subject to the terms and conditions of this Agreement, the Company grants you a limited, personal, non-exclusive, non-transferable, and non-sublicensable license to access and use the API for your own personal use, consistent with this Agreement and the Company’s Terms.
                    </li>
                    <li>
                      <b>LICENSE RESTRICTIONS.</b> The API should be installed in accordance with the instructions provided during the installation process. Except as expressly and unambiguously permitted by this Agreement, you may not, nor permit anyone else to, directly or indirectly: (i) commercially distribute, rent, lease, market, sublicense, resell or otherwise transfer the API, (ii) copy or modify the API either alone or in conjunction with any other product or program, (iii) decompile, disassemble or reverse engineer all or any portion of the API, except and only to the extent, that such activity is expressly permitted by applicable law notwithstanding this limitation, (iv) remove any identification, including copyright, trademark, patent or other notices, made in connection with the API; or (v) use the Company's name, logo or trademarks without prior written consent from the Company.
                    </li>
                    <li>
                      <b>OWNERSHIP; COPYRIGHT PROTECTION.</b> The API is licensed to you, not sold. As between the parties, all title, ownership rights, and intellectual property rights (including all copyrights, patents, trade secret rights and trademarks) in and to the API, including all documentation, shall remain in the Company, its affiliates, or their respective suppliers and/or licensors. All title and intellectual property rights in and to the content which may be accessed through use of the API, including without limitation all data and statistics developed by the Company, are the property of the Company, its affiliates, or the respective content owner.
                      You acknowledge that the API, and content accessed through the API (the “Content”) have been developed, compiled, prepared, revised, selected and arranged by the Company and others through the application of methods and standards of judgment developed and applied through the expenditure of substantial time, effort and money, and constitute valuable intellectual property of the Company. You agree to protect the proprietary rights of the Company and all others having rights in the API and the Content during and after the term of this Agreement and to comply with all reasonable written requests made by the Company or its partners and licensors of content or otherwise to protect their and others’ contractual, statutory, and common law rights in the API and the Content.
                    </li>
                    <li>
                      <b>TERM AND TERMINATION.</b> The license is effective until terminated by you or by the Company. Your rights under this Agreement will terminate automatically without notice from the Company if you fail to comply with any term(s) of this Agreement, including those set forth in the Terms and incorporated herein by reference. Upon termination of the licenses granted hereunder, you shall cease all use of the API and destroy all copies, full or partial, of the API.
                    </li>
                    <li>
                      <b>LIMITATIONS ON USE.</b> Use of the license may be limited or revoked at any time, if, in the Company’s sole discretion, use of the API exceeds a reasonable volume. You agree that you are responsible for any fees incurred for use in excess of this volume.
                    </li>
                    <li>
                      <b>SERVICES MATERIALS.</b> The API enables access to products or services and websites ("Services"). You understand that by using the API and/or any of the Services, you may be exposed to content from a variety of sources, and that the Company is not responsible for the accuracy, usefulness, safety, or intellectual property rights of or relating to such content. You agree to waive, and hereby do waive, any legal or equitable rights or remedies you may have against the Company with respect to such Services.
                    </li>
                    <li>
                      <b>COLLECTION OF INFORMATION.</b> Subject to the Company’s privacy policy, the Company may collect various types of information about you and your use of the API, including personally identifiable information and aggregate information that does not identify a particular user. Please refer to the Company’s privacy policy for more details on the collection of information.
                    </li>
                    <li>
                      <b>DISCLOSURE OF INFORMATION.</b> The Company may disclose any information you submitted via the use of the API if it has a good faith belief that disclosure of such information is reasonably necessary to (i) comply with any applicable law, regulation, legal process or governmental request, (ii) detect, prevent, or otherwise address fraud or security issues, or (iii) protect against harm to the rights, property or safety of the Company, their users, yourself or the public.
                    </li>
                  </ol>
                  <p>
                    In the event that the Company is acquired by or merged with a third-party entity, the Company reserves the right to transfer or assign the information the Company may have collected from you as part of such merger, acquisition, sale, or other change of control. In the unlikely event of the Company’s bankruptcy, insolvency, reorganization, receivership, or assignment for the benefit of creditors, or the application of laws or equitable principles affecting creditors' rights generally, the Company may not be able to control how your information is treated, transferred, or used. In any of the circumstances described in this paragraph, the information will be subject to the privacy policy of the purchaser or assignee.
                  </p>
                  <ol>
                    <li>
                      <b>DISCLAIMER OF WARRANTIES.</b> You understand that the Company cannot and does not guarantee or warrant that use of the API is free from risk, or that the API will be free of viruses or other destructive code. You are responsible for implementing sufficient procedures and checkpoints to satisfy your particular requirements for data and anti-virus protection.
                      <br /><br />
                      THE COMPANY WILL NOT BE LIABLE FOR ANY LOSS OR DAMAGE CAUSED BY YOUR USE OF THE API OR ANY FEATURES OR ITEMS OBTAINED THROUGH THE API.
                      YOUR USE OF THE API, ITS CONTENT AND ANY FEATURES OR ITEMS OBTAINED THROUGH THE API IS AT YOUR OWN RISK. THE API, ITS CONTENT AND ANY FEATURES OR ITEMS OBTAINED THROUGH THE API ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. NEITHER THE COMPANY NOR ANY PERSON ASSOCIATED WITH THE COMPANY MAKES ANY WARRANTY OR REPRESENTATION WITH RESPECT TO THE COMPLETENESS, SECURITY, RELIABILITY, QUALITY, ACCURACY OR AVAILABILITY OF THE API. WITHOUT LIMITING THE FOREGOING, NEITHER THE COMPANY NOR ANYONE ASSOCIATED WITH THE COMPANY REPRESENTS OR WARRANTS THAT THE API, ITS CONTENT OR ANY FEATURES OR ITEMS OBTAINED THROUGH THE API WILL BE ACCURATE, RELIABLE, ERROR-FREE OR UNINTERRUPTED, THAT DEFECTS WILL BE CORRECTED, THAT THE API OR THE SERVER THAT MAKES IT AVAILABLE ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS OR THAT THE API OR ANY FEATURES OR ITEMS OBTAINED THROUGH THE API WILL OTHERWISE MEET YOUR NEEDS OR EXPECTATIONS.
                      <br /><br />
                      THE COMPANY HEREBY DISCLAIMS ALL WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, STATUTORY OR OTHERWISE, INCLUDING BUT NOT LIMITED TO ANY WARRANTIES OF MERCHANTABILITY, FITNESS FOR PARTICULAR PURPOSE, NON-INFRINGEMENT, OR ARISING FROM COURSE OF DEALING, COURSE OF PERFORMANCE OR USAGE OF TRADE.
                    </li>
                    <li>
                      <b>LIMITATION OF LIABILITY.</b> IN NO EVENT WILL THE COMPANY, ITS AFFILIATES OR THEIR LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND, UNDER ANY LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE, OR INABILITY TO USE, THE API, ANY CONTENT, FEATURES OR ITEMS ACCESSED THROUGH THE API, INCLUDING ANY INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO, PERSONAL INJURY, PAIN AND SUFFERING, EMOTIONAL DISTRESS, LOSS OF REVENUE, LOSS OF PROFITS, LOSS OF BUSINESS OR ANTICIPATED SAVINGS, LOSS OF USE, LOSS OF GOODWILL, LOSS OF DATA, AND WHETHER CAUSED BY TORT (INCLUDING NEGLIGENCE), BREACH OF CONTRACT OR OTHERWISE, EVEN IF FORESEEABLE OR IF THE COMPANY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. THE MAXIMUM LIABILITY OF THE COMPANY TO ANY USER FOR ANY LOSS, CLAIM, DAMAGE, OR LIABILITY OF ANY KIND WHATSOEVER, INCLUDING DUE TO THE COMPANY’S NEGLIGENCE OR MISCONDUCT, SHALL BE LIMITED TO THE AMOUNT ACTUALLY PAID BY USER FOR USE OF THE API OR ANY PRODUCTS OR SERVICES PURCHASED THROUGH THE API DURING THE SIX-MONTH PERIOD PRECEDING THE CLAIM.
                    </li>
                    <li>
                      <b>GOVERNMENT USE.</b> If you are part of an agency, department, or other entity of the United States Government ("Government"), the use, duplication, reproduction, release, modification, disclosure or transfer of the API is restricted in accordance with the Federal Acquisition Regulations as applied to civilian agencies and the Defense Federal Acquisition Regulation Supplement as applied to military agencies. The API is a "commercial item", "commercial computer software" and "commercial computer software documentation". In accordance with such provisions, any use of the API by the Government shall be governed solely by the terms of this Agreement.
                    </li>
                    <li>
                      <b>EXPORT CONTROLS.</b> You acknowledge that the API may be subject to international rules that govern the export of software. You agree to comply with all applicable international and national laws that apply to the API as well as end-user, end-use and destination restrictions issued by national governments.
                    </li>
                    <li>
                      <b>MISCELLANEOUS.</b> If any provision of this Agreement is held to be unenforceable for any reason, such provision shall be reformed only to the extent necessary to make it enforceable. Any waiver of any provision of this Agreement will be effective only if in writing and signed by the Company. This Agreement is personal to you and may not be assigned or transferred for any reason whatsoever without the consent of the Company and any action or conduct in violation of the foregoing shall be void and without effect. This Agreement and any dispute or claim arising out of, or related to, it, its subject matter or its formation (in each case, including non-contractual disputes or claims) shall be governed by and construed in accordance with the internal laws of the State of Colorado without regard to its conflict of law principles. Those provisions that by their nature survive any expiration or termination of this Agreement shall survive beyond any such expiration or termination.
                    </li>
                  </ol>
                </CardText>
                <CardText style={{ fontSize: '13px' }}>
                  Last revised: August 5, 2022
                </CardText>
              </Col>
            </CardBody>
            <CardFooter style={{ backgroundColor: 'var(--brand-ink)' }}>
              <ButtonGroup>
                <Button
                  color="white"
                  onClick={this.generateApiKeys}
                >
                  Accept
                </Button>&nbsp;
                <Button color="white" onClick={this.termsAgreementModalClose}>
                  Decline
                </Button>
              </ButtonGroup>
            </CardFooter>
          </Card> : null
        }
      </div>
    )
  }
}
